/* @jsx jsx */
/** @jsxFrag */
import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl-next';
import { jsx } from '@emotion/react';
import LegacyButton from '@atlaskit/button/standard-button';
import Button from '@atlaskit/button/new';
import Lozenge from '@atlaskit/lozenge';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import VisuallyHidden from '@atlaskit/visually-hidden';
import Toggle from '@atlaskit/toggle';
import { xcss, Inline, Box } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import messages from './messages';
import { containerLayout, actionsButtonGroup, actionsContainer, columnLayout, footerStyles, footerInformationStyles, previewContainer, actionsContainerWrapper, diffToggleWrapper } from './styles';
import { FloatingContainer } from '../../components/FloatingContainer/FloatingContainer';
import { LatestPromptHeader } from '../../components/LatestPromptHeader/LatestPromptHeader';
import { defaultPaddingStyles } from '../../components/FloatingContainer/styles';
import { PromptForm } from '../../components/PromptForm/PromptForm';
import { CopyIconButton } from '../../components/CopyIconButton/CopyIconButton';
import { RetryIconButton } from '../../components/RetryIconButton/RetryIconButton';
import { FeedbackForm } from '../../components/FeedbackForm/FeedbackForm';
import { AI_MODAL_SCREEN_CLASS } from '../../../utils/constants';
import { buttonAutoFocusRingFix } from '../../styles/focus';
import { isExperimentEnabled, getExperimentCohort } from '../../../utils/experiments';

// Copied from packages/editor/editor-plugin-ai/src/config-items/config-items.tsx

/**
 * Could be either:
 * - userInput only (free form generate / interrogate)
 * - presetTitle only (summarize)
 * - both presetTitle and userInput (brainstorm)
 */

const mapActionAppearanceToButtonAppearance = {
  primary: 'primary',
  secondary: 'default',
  cancel: 'subtle'
};
export const PreviewScreen = ({
  latestPrompt,
  preview,
  diffPreview,
  canShowDiffPreview,
  disableActions = false,
  onSubmit,
  onCancel,
  onCopy,
  onRetry,
  onInputFocus,
  onInputBlur,
  onInputChange,
  onADFChange,
  onGoodResponseButtonClick,
  onBadResponseButtonClick,
  onEditPromptClick,
  onShowDiffPreview,
  applications,
  isInputActive,
  inputValue,
  inputADFValue,
  product,
  PromptEditor,
  clearInputRef,
  additionalFooterText,
  providerFactory,
  schema
}) => {
  const {
    formatMessage
  } = useIntl();
  const focusInputRef = useRef(undefined);
  const initialPlaceholder = formatMessage(messages.defaultInputPlaceholder);
  const [placeholder, setPlaceholder] = useState(initialPlaceholder);
  const [isDiffPreviewVisible, setIsDiffPreviewVisible] = useState(false);
  const hasValidDiffPreview = !!diffPreview;
  useEffect(() => {
    setPlaceholder(isInputActive ? '' : initialPlaceholder);
  }, [isInputActive, initialPlaceholder]);
  const handleToggleDiffPreviewVisibility = useCallback(() => {
    onShowDiffPreview === null || onShowDiffPreview === void 0 ? void 0 : onShowDiffPreview();
    setIsDiffPreviewVisible(value => !value);
  }, [setIsDiffPreviewVisible, onShowDiffPreview]);
  const removeBetaLozenge = product === 'JPD' ? getBooleanFF('platform.editor.ai-remove-beta-lozenge-jpd_1t8rt') : getBooleanFF('platform.editor.ai-remove-beta-lozenge_s5yrq');
  const usePromptEditorInInterrogate = !!getBooleanFF('platform.editor.ai-lightweight-editor-interrogate_x356n');
  const isVisualDiffPreviewEnabled = isExperimentEnabled('editor_ai_-_visual_diff_preview_experiment') && getExperimentCohort('editor_ai_-_visual_diff_preview_experiment') === 'visual-diff-preview';
  const ActionsWrapper = useCallback(({
    children
  }) => isVisualDiffPreviewEnabled ? jsx("div", {
    css: actionsContainerWrapper
  }, children) : jsx(React.Fragment, null, children), [isVisualDiffPreviewEnabled]);
  return jsx("div", {
    css: columnLayout,
    className: "preview-screen"
  }, jsx(FloatingContainer, {
    disablePadding: true,
    "data-testid": "preview-screen",
    className: `preview-screen ${AI_MODAL_SCREEN_CLASS}`,
    css: containerLayout
  }, jsx(VisuallyHidden, null, formatMessage(messages.previewScreenLabel)), jsx(LatestPromptHeader, {
    prompt: latestPrompt === null || latestPrompt === void 0 ? void 0 : latestPrompt.userInput,
    promptADF: latestPrompt === null || latestPrompt === void 0 ? void 0 : latestPrompt.userADFInput,
    presetTitle: latestPrompt === null || latestPrompt === void 0 ? void 0 : latestPrompt.presetTitle,
    providerFactory: providerFactory,
    schema: schema,
    onEditPromptClick: onEditPromptClick
  }), jsx("div", {
    css: [previewContainer, defaultPaddingStyles]
  }, isVisualDiffPreviewEnabled && isDiffPreviewVisible ? diffPreview : preview, jsx(ActionsWrapper, null, isVisualDiffPreviewEnabled && canShowDiffPreview && jsx("div", {
    css: diffToggleWrapper
  }, jsx(Tooltip, {
    content: !hasValidDiffPreview && formatMessage(messages.highlightDifferencesDisabledTooltip)
  }, jsx(Inline, {
    alignBlock: "center"
  }, jsx(Toggle, {
    isDisabled: !hasValidDiffPreview,
    label: formatMessage(messages.highlightDifferencesLabel),
    id: "diff-preview-toggle",
    testId: "diff-preview-toggle",
    isChecked: isDiffPreviewVisible,
    onChange: handleToggleDiffPreviewVisibility
  }), jsx(Box, {
    xcss: xcss({
      ...(!hasValidDiffPreview && {
        color: 'color.text.disabled',
        cursor: 'not-allowed'
      })
    }),
    as: "label",
    htmlFor: "diff-preview-toggle"
  }, jsx("span", null, formatMessage(messages.highlightDifferencesLabel)))))), jsx("div", {
    css: actionsContainer
  }, jsx(LegacyButton, {
    testId: "cancel-button",
    onClick: onCancel,
    css: buttonAutoFocusRingFix,
    autoFocus: true,
    type: "button",
    appearance: disableActions ? 'primary' : 'subtle'
  }, formatMessage(messages.cancelLabel)), disableActions ? null : jsx("div", {
    css: actionsButtonGroup
  }, jsx(RetryIconButton, {
    onClick: onRetry
  }), jsx(CopyIconButton, {
    onClick: onCopy
  }), applications.map(({
    onClick,
    label,
    appearance
  }) => getBooleanFF('platform.design-system-team.editor-new-button_jjjdo') ? jsx(Button, {
    key: label,
    testId: "insert-response-button",
    onClick: onClick,
    type: "button",
    appearance: mapActionAppearanceToButtonAppearance[appearance]
  }, label) : jsx(LegacyButton, {
    key: label,
    testId: "insert-response-button",
    onClick: onClick,
    type: "button",
    appearance: mapActionAppearanceToButtonAppearance[appearance]
  }, label)))))), disableActions ? null : jsx("div", {
    css: defaultPaddingStyles
  }, jsx(PromptForm, {
    showButtons: isInputActive,
    autoFocus: false,
    placeholder: placeholder,
    ariaLabel: initialPlaceholder,
    value: inputValue,
    adfValue: inputADFValue,
    onFormSubmit: onSubmit,
    onCancel: onCancel,
    onInputChange: onInputChange,
    onADFChange: onADFChange,
    onInputFocus: onInputFocus,
    onInputBlur: onInputBlur,
    focusInputRef: focusInputRef,
    clearInputRef: clearInputRef,
    PromptEditor: usePromptEditorInInterrogate ? PromptEditor : undefined
  })), jsx("div", {
    css: footerStyles
  }, jsx("div", {
    css: footerInformationStyles
  }, !removeBetaLozenge && jsx(Lozenge, {
    testId: "beta-lozenge",
    appearance: "inprogress"
  }, formatMessage(messages.betaLabel)), jsx("p", null, formatMessage(messages.disclaimer) + ' ', jsx("a", {
    href: "https://www.atlassian.com/trust/atlassian-intelligence",
    target: "_blank"
  }, formatMessage(messages.disclaimerLink)), additionalFooterText && jsx("strong", null, ". ", additionalFooterText))), jsx(FeedbackForm, {
    onGoodResponseButtonClick: onGoodResponseButtonClick,
    onBadResponseButtonClick: onBadResponseButtonClick
  }))));
};